import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import {
  ChatConversation,
  ChatMessagesResponse,
  ChatPersistedMessage,
  SendChatFeedbackRequest,
  SendChatMessageRequest,
} from '@blaze-dashboard/ai-chat-domain';
import { XhrService } from '@blaze-shared/platform-data-access';

@Injectable({ providedIn: 'root' })
export class ChatService {
  private readonly http = inject(XhrService);

  sendMessage(chatId: string, message: SendChatMessageRequest): Observable<ChatPersistedMessage> {
    return this.http.post<ChatPersistedMessage>(`ai/chats/${chatId}/message`, message);
  }

  loadChatConversations(projectId: string): Observable<ChatConversation[]> {
    return this.http.get<ChatConversation[]>(`ai/chats`, { projectId });
  }

  loadLatestChatConversation(projectId: string): Observable<ChatConversation | undefined> {
    return this.loadChatConversations(projectId).pipe(map((chats) => chats.at(0)));
  }

  createChatConversation(projectId: string): Observable<ChatConversation> {
    return this.http.post<ChatConversation>(`ai/chats`, undefined, { projectId });
  }

  loadMessages(
    chatId: string,
    options: { afterMessageId?: string; take?: number } = {},
  ): Observable<ChatMessagesResponse> {
    return this.http.get<ChatMessagesResponse>(`ai/chats/${chatId}/messages`, { ...options });
  }

  interruptChat(chatId: string): Observable<void> {
    return this.http.post<void>(`ai/chats/${chatId}/interrupt`, undefined);
  }

  sendFeedback(
    chatId: string,
    messageId: string,
    feedback: SendChatFeedbackRequest,
  ): Observable<void> {
    return this.http.post<void>(`ai/chats/${chatId}/feedback`, { messageId, ...feedback });
  }
}
